import React from 'react';
import { MessageTokenUsage, formatTokens, formatCost, formatLatency } from '../../services/tokenTracker';
import { ArrowUpIcon, ArrowDownIcon, ZapIcon, ClockIcon, LayersIcon, CpuIcon, DollarSignIcon, CheckCircle2Icon, XIcon } from 'lucide-react';
interface TokenUsagePopoverProps {
  usage: MessageTokenUsage;
  onClose: () => void;
}
export function TokenUsagePopover({
  usage,
  onClose
}: TokenUsagePopoverProps) {
  return <div className="absolute right-0 bottom-full mb-2 w-72 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl shadow-black/60 z-50 animate-in fade-in slide-in-from-bottom-2 duration-150 overflow-hidden" data-id="element-2995">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-slate-800/50" data-id="element-2996">
        <div className="flex items-center gap-2" data-id="element-2997">
          <CpuIcon size={14} className="text-indigo-400" data-id="element-2998" />
          <span className="text-sm font-semibold text-slate-200" data-id="element-2999">
            Token Usage
          </span>
        </div>
        <button onClick={onClose} className="p-1 rounded-md text-slate-500 hover:text-slate-300 hover:bg-slate-700 transition-colors" data-id="element-3000">
          <XIcon size={14} data-id="element-3001" />
        </button>
      </div>

      {/* Main Stats */}
      <div className="p-4 space-y-3" data-id="element-3002">
        {/* Input/Output Tokens */}
        <div className="grid grid-cols-2 gap-3" data-id="element-3003">
          <div className="bg-slate-800/60 rounded-lg p-3 border border-slate-700/50" data-id="element-3004">
            <div className="flex items-center gap-1.5 text-emerald-400 mb-1" data-id="element-3005">
              <ArrowUpIcon size={12} data-id="element-3006" />
              <span className="text-[10px] font-medium uppercase tracking-wider" data-id="element-3007">
                Input
              </span>
            </div>
            <div className="text-lg font-bold text-slate-100" data-id="element-3008">
              {formatTokens(usage.total_prompt_tokens)}
            </div>
          </div>
          <div className="bg-slate-800/60 rounded-lg p-3 border border-slate-700/50" data-id="element-3009">
            <div className="flex items-center gap-1.5 text-blue-400 mb-1" data-id="element-3010">
              <ArrowDownIcon size={12} data-id="element-3011" />
              <span className="text-[10px] font-medium uppercase tracking-wider" data-id="element-3012">
                Output
              </span>
            </div>
            <div className="text-lg font-bold text-slate-100" data-id="element-3013">
              {formatTokens(usage.total_completion_tokens)}
            </div>
          </div>
        </div>

        {/* Summary Row */}
        <div className="flex items-center justify-between text-xs text-slate-400 px-1" data-id="element-3014">
          <div className="flex items-center gap-1.5" data-id="element-3015">
            <LayersIcon size={11} data-id="element-3016" />
            <span data-id="element-3017">Total: {formatTokens(usage.total_tokens)} tokens</span>
          </div>
          <div className="flex items-center gap-1.5 text-amber-400" data-id="element-3018">
            <DollarSignIcon size={11} data-id="element-3019" />
            <span data-id="element-3020">{formatCost(usage.estimated_cost_usd)}</span>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-slate-800" data-id="element-3021" />

        {/* Metadata */}
        <div className="space-y-2" data-id="element-3022">
          <div className="flex items-center justify-between text-xs" data-id="element-3023">
            <span className="text-slate-500" data-id="element-3024">Rounds</span>
            <span className="text-slate-300 font-medium" data-id="element-3025">
              {usage.total_rounds}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs" data-id="element-3026">
            <span className="text-slate-500" data-id="element-3027">Tool Calls</span>
            <span className="text-slate-300 font-medium" data-id="element-3028">
              {usage.total_tool_calls}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs" data-id="element-3029">
            <span className="text-slate-500" data-id="element-3030">Latency</span>
            <span className="text-slate-300 font-medium flex items-center gap-1" data-id="element-3031">
              <ClockIcon size={10} data-id="element-3032" />
              {formatLatency(usage.latency_ms)}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs" data-id="element-3033">
            <span className="text-slate-500" data-id="element-3034">Classifier</span>
            <span className={`font-medium flex items-center gap-1 ${usage.classifier_active ? 'text-emerald-400' : 'text-slate-500'}`} data-id="element-3035">
              {usage.classifier_active ? <>
                  <CheckCircle2Icon size={10} data-id="element-3036" />
                  Active
                </> : 'Inactive'}
            </span>
          </div>
        </div>

        {/* Services Used */}
        {usage.services_used.length > 0 && <>
            <div className="h-px bg-slate-800" data-id="element-3037" />
            <div data-id="element-3038">
              <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500 mb-2" data-id="element-3039">
                Services
              </div>
              <div className="flex flex-wrap gap-1.5" data-id="element-3040">
                {usage.services_used.map(service => <span key={service} className="px-2 py-0.5 bg-indigo-950/50 border border-indigo-800/40 rounded text-[10px] text-indigo-300 font-medium" data-id="element-3041">
                    {service}
                  </span>)}
              </div>
            </div>
          </>}

        {/* Rounds Breakdown (if multiple) */}
        {usage.rounds.length > 1 && <>
            <div className="h-px bg-slate-800" data-id="element-3042" />
            <div data-id="element-3043">
              <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500 mb-2" data-id="element-3044">
                Round Breakdown
              </div>
              <div className="space-y-1.5 max-h-24 overflow-y-auto" data-id="element-3045">
                {usage.rounds.map((round, idx) => <div key={idx} className="flex items-center justify-between text-[10px] bg-slate-800/40 rounded px-2 py-1.5" data-id="element-3046">
                    <span className="text-slate-500" data-id="element-3047">Round {idx + 1}</span>
                    <div className="flex items-center gap-3" data-id="element-3048">
                      <span className="text-emerald-400" data-id="element-3049">
                        ↑{formatTokens(round.prompt_tokens)}
                      </span>
                      <span className="text-blue-400" data-id="element-3050">
                        ↓{formatTokens(round.completion_tokens)}
                      </span>
                      {round.tool_calls > 0 && <span className="text-amber-400 flex items-center gap-0.5" data-id="element-3051">
                          <ZapIcon size={9} data-id="element-3052" />
                          {round.tool_calls}
                        </span>}
                    </div>
                  </div>)}
              </div>
            </div>
          </>}
      </div>
    </div>;
}